import { ChatFirestorePaths, Conversation } from '@quadminds/shared/stork';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getChatAuth, getChatFirestore } from './chat-firebase';

interface FirestoreMessage {
    text: string;
    senderId: string;
    timestamp: string;
}

export interface UseDriverConversationsOptions {
    /**
     * When true, returns `baseConversations` unchanged without subscribing to Firestore.
     * Set via a dev flag to work without Firestore during local development.
     */
    useMock?: boolean;
}

export interface UseDriverConversationsResult {
    conversations: Conversation[];
    /** True until every conversation has received its first Firestore snapshot at least once. */
    isLoading: boolean;
    /** Applies a local, optimistic patch to an existing conversation (e.g. unreadCount: 0 on select). */
    patchConversation: (id: string, patch: Partial<Conversation>) => void;
    /** Inserts a conversation if it's not present yet, or merges the patch if it already is. */
    upsertConversation: (conversation: Conversation) => void;
}

const toTimestamp = (value: Date | string | undefined): number | null => {
    if (!value) return null;
    const time = value instanceof Date ? value.getTime() : Date.parse(value);
    return isNaN(time) ? null : time;
};

/**
 * Subscribes to each conversation's `rooms/{id}/messages` in Firestore and enriches
 * `baseConversations` with a live `lastMessage`/`lastMessageTime` preview and `unreadCount`
 * (messages sent after `conv.lastReadAt`, the Mongo-backed watermark; everything counts as
 * unread when it's not set yet).
 *
 * When `useMock` is true, the base conversations are returned as-is and isLoading is false.
 */
export const useDriverConversations = (
    baseConversations: Conversation[],
    options?: UseDriverConversationsOptions,
): UseDriverConversationsResult => {
    const [conversations, setConversations] = useState<Conversation[]>(baseConversations);
    // Most recent first. Conversations with no lastMessageTime stay on top: those are the ones just
    // created via upsertConversation, which have no message yet and must stay visible to the driver.
    const sortedConversations = useMemo(
        () =>
            [...conversations].sort((a, b) => {
                const aTime = toTimestamp(a.lastMessageTime);
                const bTime = toTimestamp(b.lastMessageTime);
                if (aTime === null && bTime === null) return 0;
                if (aTime === null) return -1;
                if (bTime === null) return 1;
                return bTime - aTime;
            }),
        [conversations],
    );
    const [isLoading, setIsLoading] = useState(baseConversations.length > 0);

    const everLoadedConvIdsRef = useRef<Set<string>>(new Set());

    const baseIdsKey = baseConversations.map((c) => c.id).join(',');

    useEffect(() => {
        if (options?.useMock) {
            setConversations(baseConversations);
            setIsLoading(false);
            return;
        }

        if (!baseConversations.length) {
            setIsLoading(false);
            return;
        }

        let firestore: ReturnType<typeof getChatFirestore>;
        try {
            firestore = getChatFirestore();
        } catch {
            // initChatFirebase not yet called; isLoading stays true until it resolves and this re-runs
            return;
        }

        // Seed with base conversations so the list renders immediately
        setConversations(baseConversations);
        setIsLoading(baseConversations.some((conv) => !everLoadedConvIdsRef.current.has(conv.id)));

        const markConvLoaded = (convId: string) => {
            if (everLoadedConvIdsRef.current.has(convId)) return;
            everLoadedConvIdsRef.current.add(convId);
            setIsLoading(baseConversations.some((conv) => !everLoadedConvIdsRef.current.has(conv.id)));
        };

        let cancelled = false;
        let unsubscribes: (() => void)[] = [];

        getChatAuth().then((auth) => {
            if (cancelled) return;
            const currentUserId = auth?.currentUser?.uid;

            unsubscribes = baseConversations.flatMap((conv) => {
                const messagesRef = collection(firestore, `${ChatFirestorePaths.ROOMS}/${conv.id}/messages`);

                const unsubLastMessage = onSnapshot(
                    query(messagesRef, orderBy('timestamp', 'desc'), limit(1)),
                    (snapshot) => {
                        markConvLoaded(conv.id);
                        const lastMessageDoc = snapshot.docs[0]?.data() as FirestoreMessage | undefined;
                        if (!lastMessageDoc) return;

                        setConversations((prev) =>
                            prev.map((c) =>
                                c.id === conv.id
                                    ? {
                                          ...c,
                                          lastMessage: lastMessageDoc.text,
                                          lastMessageTime: new Date(lastMessageDoc.timestamp),
                                      }
                                    : c,
                            ),
                        );
                    },
                );

                const unsubUnreadCount = onSnapshot(messagesRef, (snapshot) => {
                    markConvLoaded(conv.id);
                    setConversations((prev) =>
                        prev.map((c) => {
                            if (c.id !== conv.id) return c;
                            const unreadCount = snapshot.docs.filter((doc) => {
                                const message = doc.data() as FirestoreMessage;
                                return (
                                    message.senderId !== currentUserId &&
                                    (!c.lastReadAt || message.timestamp > c.lastReadAt)
                                );
                            }).length;
                            return { ...c, unreadCount };
                        }),
                    );
                });

                return [unsubLastMessage, unsubUnreadCount];
            });
        });

        return () => {
            cancelled = true;
            unsubscribes.forEach((unsub) => unsub());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseIdsKey, options?.useMock]);

    const patchConversation = (id: string, patch: Partial<Conversation>) => {
        setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    };

    const upsertConversation = (conversation: Conversation) => {
        setConversations((prev) =>
            prev.some((c) => c.id === conversation.id)
                ? prev.map((c) => (c.id === conversation.id ? { ...c, ...conversation } : c))
                : [conversation, ...prev],
        );
    };

    return { conversations: sortedConversations, isLoading, patchConversation, upsertConversation };
};
